import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, gbp, airlineLogo } from "@/lib/api";
import { SearchWidget } from "@/components/SearchWidget";
import { HeroSlideshow } from "@/components/HeroSlideshow";
import { HeroVideo } from "@/components/HeroVideo";
import { FlightCard } from "@/components/FlightCard";
import { FareStrip } from "@/components/FareStrip";
import { TrustRibbon } from "@/components/TrustRibbon";
import { HotelCard } from "@/components/HotelCard";
import { HolidayCard } from "@/components/HolidayCard";
import { Button } from "@/components/ui/button";
import { ShieldCheck, BadgePoundSterling, Headphones, PlaneTakeoff, ArrowRight, Star, BadgeCheck, Zap, Landmark, Phone, MessageCircle } from "lucide-react";

const trust = [
  { icon: ShieldCheck, title: "100% Secure Booking", desc: "SSL-encrypted checkout" },
  { icon: BadgePoundSterling, title: "Best Price Promise", desc: "Exclusive trade & net fares" },
  { icon: Headphones, title: "24/7 Support", desc: "Real people, whenever you need" },
  { icon: PlaneTakeoff, title: "600+ Airlines", desc: "Worldwide flights & packages" },
];

const destinations = [
  { name: "Dubai", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c", price: 351, span: "md:col-span-8 md:row-span-2" },
  { name: "Istanbul", img: "https://images.unsplash.com/photo-1527838832700-5059252407fa", price: 199, span: "md:col-span-4" },
  { name: "Islamabad", img: "https://images.unsplash.com/photo-1623261216813-fbbe4fb7df44", price: 489, span: "md:col-span-4" },
  { name: "Makkah", img: "https://images.unsplash.com/photo-1513072064285-240f87fa81e8", price: 412, span: "md:col-span-4" },
  { name: "Lahore", img: "https://images.unsplash.com/photo-1684439061252-cb6632acb8ce", price: 499, span: "md:col-span-4" },
  { name: "Amritsar", img: "https://images.unsplash.com/photo-1623059508779-2542c6e83753", price: 545, span: "md:col-span-4" },
];

const LOGO_CODES = ["BA", "VS", "EK", "QR", "EY", "TK", "LH", "AF", "KL", "SQ", "QF", "AI", "CX", "SV", "TG", "MH"];

const reviews = [
  ["Aisha Khan", "AK", "Birmingham", "Booked our Umrah package — flawless from visa to 5★ Haram-view hotel. The team held our hands the whole way. Truly exceptional.", "2 weeks ago"],
  ["David Miller", "DM", "London", "Cheapest Dubai flights I could find anywhere. Instant confirmation email and brilliant support at 11pm. Will book again.", "1 month ago"],
  ["Priya Sharma", "PS", "Leicester", "Family holiday to Antalya sorted in 10 minutes and a voucher saved us £180. Genuinely the best value online.", "3 weeks ago"],
];

export default function Home() {
  const [deals, setDeals] = useState({ flights: [], hotels: [], holidays: [] });
  const [bIdx, setBIdx] = useState(0);
  const nav = useNavigate();
  useEffect(() => { api.get("/deals").then(({ data }) => setDeals(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!deals.holidays.length) return;
    const t = setInterval(() => setBIdx((i) => (i + 1) % deals.holidays.length), 5000);
    return () => clearInterval(t);
  }, [deals.holidays.length]);

  return (
    <div className="bg-[#070B14] text-white -mt-16">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[88vh] min-h-[640px]">
          <HeroSlideshow />
          <HeroVideo />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070B14]/70 via-[#070B14]/40 to-[#070B14]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070B14]/80 to-transparent" />
          <div className="absolute inset-0 cine-grain opacity-40" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-center pt-16">
            <div className="inline-flex items-center gap-2 self-start bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-4 py-1.5 mb-5 animate-fade-up">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-medium text-slate-100">Trusted by 12,000+ UK travellers</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold max-w-3xl leading-[1.02] text-glow animate-fade-up">
              The world is<br /><span className="text-[#FF6B35]">yours to explore</span>
            </h1>
            <p className="text-slate-200 text-lg mt-5 max-w-xl animate-fade-up">
              Flights, hotels, holidays, car hire and Umrah — booked in minutes, at fares the big sites can't match.
            </p>
            <div className="flex flex-wrap gap-3 mt-7 animate-fade-up">
              <Button onClick={() => nav("/flights")} data-testid="hero-live-btn" className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold px-7 py-6 text-base">
                <Zap className="w-5 h-5 mr-2" /> Search flights
              </Button>
              <Button onClick={() => nav("/holidays")} variant="outline" className="rounded-full bg-white/5 border-white/25 text-white hover:bg-white/15 hover:text-white font-semibold px-7 py-6 text-base">
                Explore holidays
              </Button>
            </div>
          </div>
        </div>
        {/* Search widget overlapping */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-28 relative z-20">
          <SearchWidget />
        </div>
      </section>

      {/* Trust ribbon */}
      <TrustRibbon />

      {/* Today's lowest fares ticker */}
      <FareStrip />

      {/* Seasonal Umrah banner */}
      <section data-testid="umrah-season-banner" className="max-w-7xl mx-auto px-4 sm:px-6 mt-10">
        <div className="relative overflow-hidden rounded-2xl ring-1 ring-[#FF6B35]/30 bg-gradient-to-r from-[#0B3C5D] via-[#0e2a3d] to-[#070B14]">
          <div className="absolute inset-0 cine-grain opacity-30" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-5 px-6 py-6 md:px-8">
            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-2 bg-[#FF6B35] text-white text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1 mb-2">
                <Landmark className="w-3.5 h-3.5" /> Ramadan &amp; Easter 2026 · Limited seats
              </span>
              <h3 className="font-display text-xl md:text-2xl font-bold text-white leading-tight">
                Umrah packages from <span className="text-[#FF6B35]">£899pp</span> — departures Feb–Apr 2026
              </h3>
              <p className="text-slate-300 text-sm mt-1">5★ Haram-view hotels · fast-track visas · guided Ziyarat · return flights included.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 shrink-0">
              <a href="tel:02046153141" data-testid="umrah-call-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-sm px-4 py-2.5 transition-colors">
                <Phone className="w-4 h-4 text-[#FF6B35]" /> Call now
              </a>
              <a href="https://wa.me/442046153141?text=Hi%20Elia%20Travel%2C%20I'd%20like%20a%20quote%20for%20an%20Umrah%20package" target="_blank" rel="noopener noreferrer" data-testid="umrah-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1eb855] text-white font-semibold text-sm px-4 py-2.5 transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <Button onClick={() => nav("/umrah")} data-testid="umrah-banner-cta"
                className="rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold text-sm px-5 py-2.5 h-auto">
                View packages <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Deal of the Week banner */}
      {deals.holidays.length > 0 && (() => {
        const d = deals.holidays[bIdx];
        return (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16" data-testid="deal-banner">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 min-h-[300px] flex items-center">
              {deals.holidays.map((h, i) => (
                <img key={h.id} src={h.image} alt="" aria-hidden
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                  style={{ opacity: i === bIdx ? 1 : 0 }} />
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-[#070B14] via-[#070B14]/85 to-transparent" />
              <div className="relative px-8 py-12 md:px-14 max-w-xl">
                <span className="inline-flex items-center gap-2 bg-[#FF6B35] text-white text-[11px] font-bold uppercase tracking-widest rounded-full px-3 py-1 mb-4">
                  <Zap className="w-3.5 h-3.5" /> Deal of the week
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight" data-testid="deal-title">{d.title}</h2>
                <p className="text-slate-300 mt-2">{d.nights} nights · {d.board} · {d.destination}, {d.country}</p>
                <div className="flex items-center gap-4 mt-5">
                  <div><span className="text-slate-400 text-xs block">from</span><span className="font-display text-3xl font-bold text-[#FF6B35]">{gbp(d.price)}</span><span className="text-slate-400 text-xs"> pp</span></div>
                  <Button onClick={() => nav(`/holidays?destination=${d.destination}`)} data-testid="deal-cta" className="rounded-full bg-white text-[#0B3C5D] hover:bg-slate-100 font-semibold px-6 py-6">
                    Book this deal <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="flex gap-1.5 mt-6">
                  {deals.holidays.map((_, i) => (
                    <button key={i} onClick={() => setBIdx(i)} aria-label={`deal ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === bIdx ? "w-8 bg-[#FF6B35]" : "w-2 bg-white/30"}`} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Airline logo marquee */}
      <section className="pt-14 pb-6 overflow-hidden">
        <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-6">Fares from 600+ trusted airlines</p>
        <div className="relative">
          <div className="flex gap-4 w-max animate-marquee">
            {[...LOGO_CODES, ...LOGO_CODES].map((c, i) => (
              <div key={i} className="bg-white rounded-xl px-5 py-3 flex items-center justify-center shrink-0 shadow-sm">
                <img src={airlineLogo(c)} alt={c} className="h-6 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {trust.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 flex items-start gap-3 hover:bg-white/[0.07] transition-colors">
                <span className="w-10 h-10 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#FF6B35]" /></span>
                <div><p className="font-semibold text-white text-sm">{t.title}</p><p className="text-xs text-slate-400 mt-0.5">{t.desc}</p></div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Destinations Bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-8">
          <p className="text-[#FF6B35] font-bold uppercase tracking-widest text-xs">Where to?</p>
          <h2 className="font-display text-4xl font-bold text-white mt-1">Trending destinations</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[190px] gap-4">
          {destinations.map((d) => (
            <button key={d.name} onClick={() => nav(`/holidays?destination=${d.name}`)}
              data-testid={`dest-${d.name.toLowerCase()}`}
              className={`group relative rounded-2xl overflow-hidden text-left ring-1 ring-white/10 ${d.span}`}>
              <img src={d.img} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div><p className="text-white font-display font-bold text-2xl">{d.name}</p><p className="text-slate-300 text-xs">Flights from</p></div>
                <span className="bg-[#FF6B35] rounded-full px-3 py-1 text-sm font-bold text-white">{gbp(d.price)}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Flight deals */}
      <section className="py-14 bg-gradient-to-b from-transparent to-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-7">
            <div><p className="text-[#FF6B35] font-bold uppercase tracking-widest text-xs">Featured</p><h2 className="font-display text-4xl font-bold text-white mt-1">Today's flight deals</h2></div>
            <Button variant="ghost" onClick={() => nav("/flights")} className="text-white hover:text-[#FF6B35] hover:bg-white/5 font-semibold">View all <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {deals.flights.slice(0, 6).map((f) => <FlightCard key={f.id} f={f} />)}
          </div>
        </div>
      </section>

      {/* Holidays */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex items-end justify-between mb-7">
          <div><p className="text-[#FF6B35] font-bold uppercase tracking-widest text-xs">Getaways</p><h2 className="font-display text-4xl font-bold text-white mt-1">Holiday packages</h2></div>
          <Button variant="ghost" onClick={() => nav("/holidays")} className="text-white hover:text-[#FF6B35] hover:bg-white/5 font-semibold">View all <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {deals.holidays.slice(0, 6).map((h) => <HolidayCard key={h.id} h={h} />)}
        </div>
      </section>

      {/* Hotels */}
      <section className="py-14 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-7">
            <div><p className="text-[#FF6B35] font-bold uppercase tracking-widest text-xs">Stay</p><h2 className="font-display text-4xl font-bold text-white mt-1">Featured hotels</h2></div>
            <Button variant="ghost" onClick={() => nav("/hotels")} className="text-white hover:text-[#FF6B35] hover:bg-white/5 font-semibold">View all <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {deals.hotels.slice(0, 4).map((h) => <HotelCard key={h.id} h={h} />)}
          </div>
        </div>
      </section>

      {/* Umrah CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10">
          <img src="https://images.unsplash.com/photo-1591604466107-ec97de577aff" alt="Umrah" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070B14] via-[#070B14]/85 to-[#070B14]/30" />
          <div className="relative px-8 py-16 md:px-16 max-w-2xl">
            <p className="text-[#FF6B35] font-bold uppercase tracking-widest text-xs mb-3">Spiritual Journeys</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white">Umrah &amp; Hajj packages tailored for you</h2>
            <p className="text-slate-200 mt-3">5★ Haram-view hotels, visa processing, guided Ziyarat and return flights — from £899pp.</p>
            <Button onClick={() => nav("/umrah")} data-testid="home-umrah-cta" className="mt-6 rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold px-7 py-6">
              Explore packages <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#00b67a] rounded-md px-3 py-1.5 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-white text-white" />)}
              <span className="text-white font-semibold text-sm">Excellent</span>
            </div>
            <h2 className="font-display text-4xl font-bold text-white">Rated 4.9 / 5 by travellers</h2>
            <p className="text-slate-400 text-sm mt-2">Based on 1,284 independent verified reviews</p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {reviews.map(([n, ini, city, q, when]) => (
              <div key={n} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:bg-white/[0.06] transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#328CC1] to-[#0B3C5D] text-white font-semibold flex items-center justify-center text-sm">{ini}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-white text-sm flex items-center gap-1">{n} <BadgeCheck className="w-4 h-4 text-[#00b67a]" /></p>
                    <p className="text-xs text-slate-400">{city} · {when}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-2">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#00b67a] text-[#00b67a]" />)}</div>
                <p className="text-slate-300 text-sm leading-relaxed">"{q}"</p>
                <p className="text-[11px] text-[#00b67a] font-semibold mt-3 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Verified booking</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
